import { useCallback } from "react";

import {
  STT_ERROR_CODE_MICROPHONE_ACCESS_DENIED,
  STT_ERROR_CODE_MICROPHONE_UNAVAILABLE,
  STT_ERROR_CODE_RECORDING
} from "./SpeechToTextRecorderError";
import {
  STT_RECORDER_STATE_IDLE,
  STT_RECORDER_STATE_PAUSED,
  STT_RECORDER_STATE_RECORDING,
  STT_RECORDER_STATE_TRANSCRIBING
} from "./useSpeechToTextRecorder";
import nowInMs from "../Utils/nowInMs";

const startRecordingErrorCode = error => {
  if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
    return STT_ERROR_CODE_MICROPHONE_ACCESS_DENIED;
  }

  return STT_ERROR_CODE_MICROPHONE_UNAVAILABLE;
};

// This hook exposes the recorder actions backed by MediaRecorder state changes.
const useSpeechToTextRecorderActions = ({
  appendRecordingChunk,
  clearMediaRecorder,
  createMediaRecorder,
  disabled,
  isMediaRecorderActive,
  markDiscardNextRecording,
  mediaRecorderRef,
  mediaStreamRef,
  onError,
  onRecordingSessionStart,
  processRecordedAudio,
  recordingStartedAtRef,
  resetRecordingSession,
  resourceId,
  setRecorderState,
  startSignalMonitor,
  stopMediaStreamTracks,
  stopSignalMonitor
}) => {
  const cleanupRecordingResources = useCallback(() => {
    clearMediaRecorder();
    stopSignalMonitor();
    stopMediaStreamTracks();
    recordingStartedAtRef.current = null;
  }, [
    clearMediaRecorder,
    recordingStartedAtRef,
    stopMediaStreamTracks,
    stopSignalMonitor
  ]);

  const setupMediaRecorderHandlers = useCallback(
    mediaRecorder => {
      mediaRecorder.ondataavailable = appendRecordingChunk;
      mediaRecorder.onpause = () => setRecorderState(STT_RECORDER_STATE_PAUSED);
      mediaRecorder.onresume = () => setRecorderState(STT_RECORDER_STATE_RECORDING);
      mediaRecorder.onstop = processRecordedAudio;
      mediaRecorder.onerror = () => {
        cleanupRecordingResources();
        onError?.(STT_ERROR_CODE_RECORDING);
        setRecorderState(STT_RECORDER_STATE_IDLE);
      };
    },
    [
      appendRecordingChunk,
      cleanupRecordingResources,
      onError,
      processRecordedAudio,
      setRecorderState
    ]
  );

  const startRecording = useCallback(async () => {
    if (disabled || !resourceId) return false;

    try {
      const mediaRecorder = await createMediaRecorder();
      if (!mediaRecorder) return false;

      resetRecordingSession();
      stopSignalMonitor();
      startSignalMonitor(mediaStreamRef.current);

      setupMediaRecorderHandlers(mediaRecorder);

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      recordingStartedAtRef.current = nowInMs();
      onRecordingSessionStart?.();
      setRecorderState(STT_RECORDER_STATE_RECORDING);

      return true;
    } catch (error) {
      cleanupRecordingResources();
      onError?.(startRecordingErrorCode(error));
      setRecorderState(STT_RECORDER_STATE_IDLE);
      return false;
    }
  }, [
    cleanupRecordingResources,
    createMediaRecorder,
    disabled,
    mediaRecorderRef,
    mediaStreamRef,
    onError,
    onRecordingSessionStart,
    recordingStartedAtRef,
    resetRecordingSession,
    resourceId,
    setRecorderState,
    setupMediaRecorderHandlers,
    startSignalMonitor,
    stopSignalMonitor
  ]);

  const finalizeRecording = useCallback(() => {
    if (!isMediaRecorderActive()) return;

    const mediaRecorder = mediaRecorderRef.current;
    setRecorderState(STT_RECORDER_STATE_TRANSCRIBING);

    if (typeof mediaRecorder.requestData === "function") {
      try {
        // Flush buffered data before stop when runtime supports it.
        mediaRecorder.requestData();
      } catch {
        // Some browser engines throw if requestData is unavailable at runtime.
      }
    }

    mediaRecorder.stop();
  }, [isMediaRecorderActive, mediaRecorderRef, setRecorderState]);

  const pauseRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== STT_RECORDER_STATE_RECORDING) return;

    mediaRecorder.pause();
  }, [mediaRecorderRef]);

  const resumeRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state !== STT_RECORDER_STATE_PAUSED) return;

    mediaRecorder.resume();
  }, [mediaRecorderRef]);

  const discardCurrentRecording = useCallback(() => {
    if (!isMediaRecorderActive()) return;

    const mediaRecorder = mediaRecorderRef.current;
    markDiscardNextRecording();
    setRecorderState(STT_RECORDER_STATE_IDLE);
    mediaRecorder.stop();
  }, [
    isMediaRecorderActive,
    markDiscardNextRecording,
    mediaRecorderRef,
    setRecorderState
  ]);

  // Restart swaps the underlying MediaRecorder while keeping the recording
  // state and the mic stream alive. We detach `ondataavailable` and `onstop`
  // before stopping the old recorder so:
  //   - the standard processing chain (which transitions to IDLE and stops
  //     the media stream tracks) does not fire — no UI flicker, and the
  //   - any late data event from the dying recorder cannot leak chunks into
  //     the next recording's buffer.
  // `startRecording` then creates a fresh MediaRecorder on the existing
  // stream and resets the timer; state stays at RECORDING throughout.
  const restartRecording = useCallback(() => {
    if (!isMediaRecorderActive()) {
      startRecording();
      return;
    }

    const mediaRecorder = mediaRecorderRef.current;
    mediaRecorder.ondataavailable = null;
    mediaRecorder.onstop = null;
    mediaRecorder.stop();

    // Reset the timer synchronously so the user sees 00:00 the moment they
    // confirm the restart, not when the async startRecording chain finally
    // gets to it.
    onRecordingSessionStart?.();

    startRecording();
  }, [isMediaRecorderActive, mediaRecorderRef, onRecordingSessionStart, startRecording]);

  return {
    discardCurrentRecording,
    finalizeRecording,
    pauseRecording,
    restartRecording,
    resumeRecording,
    startRecording
  };
};

export default useSpeechToTextRecorderActions;
